// src/DoctorsList.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";
import "./alert-styles.css";
import { checkAuthAndRedirect } from "./utils";

// 🌐 Default Coordinates for Fallback
const DEFAULT_LATITUDE = 37.4219983;
const DEFAULT_LONGITUDE = -122.084;
const DEFAULT_RADIUS = "20000";

const DoctorsList = () => {

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [userLocation, setUserLocation] = useState({
    latitude: DEFAULT_LATITUDE,
    longitude: DEFAULT_LONGITUDE,
    hasFetched: false,
  });
  const [loadingDoctorId, setLoadingDoctorId] = useState(null);
  const navigate = useNavigate();

  // read user from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("tokenly_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  const handleViewProfile = async (doctorId) => {
    if (!checkAuthAndRedirect(navigate)) {
      return;
    }
    setLoadingDoctorId(doctorId);
    // Simulate navigation delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    navigate(`/doctor/${doctorId}`);
  };

  // ✅ CORE LOGIC: Determines Doctor Status (Available, Ideal, Not Available)
  const getDoctorStatus = useCallback((doc) => {
    // Status Display Logic (matches mobile app lines 280-286)
    // IMPORTANT: current_token can be 0, which is a valid token number, so check explicitly for null
    if (doc.current_token !== null) {
      return { text: "Available", color: "available", bookable: true };
    } else {
      return { text: "Ideal", color: "idle", bookable: true };
    }
  }, []);

  // ✅ BOOKING LOGIC: Determines if Generate Token button should show (matches mobile app lines 300-320)
  const isBookable = useCallback((doc) => {
    const slots = doc.hours_detail?.time || [];
    const isClosed = doc.hours_detail?.is_closed === 1;
    const hasNoSlots = slots.length === 0;
    const hasEmptyHoursDetail = (!doc.hours_detail) || (Array.isArray(doc.hours_detail) && doc.hours_detail.length === 0);

    if (isClosed || hasNoSlots || hasEmptyHoursDetail) {
      return false;
    }

    return true;
  }, []);

  // 🌐 Function to Fetch Doctors
  const fetchDoctors = useCallback(async (lat, lon) => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch("https://tokenly.biswaas.com/api/doctor-list", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          doctorName: "",
          latitude: lat,
          longitude: lon,
          radius: DEFAULT_RADIUS,
          specialties: [],
          a: "a",
        }),
      });

      const json = await res.json();
      const list = Array.isArray(json.data?.rows) ? json.data.rows : [];
      setDoctors(list);
    } catch (e) {
      setError(e.message || "Error fetching doctors");
    } finally {
      setLoading(false);
    }
  }, []);

  // 📍 useEffect to Handle Location & Initial Fetch
  useEffect(() => {
    const success = (position) => {
      const { latitude, longitude } = position.coords;
      setUserLocation({ latitude, longitude, hasFetched: true });
      fetchDoctors(latitude, longitude);
    };

    const error = (err) => {
      console.warn(`Geolocation Error(${err.code}): ${err.message}`);
      setUserLocation({
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        hasFetched: true,
      });
      fetchDoctors(DEFAULT_LATITUDE, DEFAULT_LONGITUDE);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      });
    } else {
      setUserLocation({
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        hasFetched: true,
      });
      fetchDoctors(DEFAULT_LATITUDE, DEFAULT_LONGITUDE);
    }
  }, [fetchDoctors]);

  // --- Render Handling for Location/Loading States ---

  // Don't render if user is a doctor (roleId === 3)
  if (user?.roleId === 3) {
    return null;
  }

  if (!userLocation.hasFetched) {
    return (
      <p className="text-center py-5">
        Requesting your location to find nearby doctors...
      </p>
    );
  }

  if (loading) {
    return (
      <section className="py-5">
        <div className="container text-center">
          <p>Loading doctors...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5">
        <div className="container text-center text-danger">
          <p>{error}</p>
        </div>
      </section>
    );
  }

  const visibleDoctors = doctors.slice(0, 3); // first 3 for now

  return (
    <section id="doctors-list" className="py-5 bg-light">
      <div className="container-xl">
        <div className="text-center mb-5">
          <h2 className="h3 fw-bold text-dark">Available Doctors Near your location</h2>
        </div>

        <div className="row justify-content-center">
          {visibleDoctors.map((doc) => {
            const name = doc.name_title ? `${doc.name_title} ${doc.fullname}` : doc.fullname;
            const specialtiesArr =
              doc.specialties && doc.specialties !== "null"
                ? JSON.parse(doc.specialties)
                : [];
            const speciality = specialtiesArr[0] || "Doctor";
            const slots = doc.hours_detail?.time || [];
            const photo = doc.profile_photo_full;
            const statusInfo = getDoctorStatus(doc);
            const canBook = isBookable(doc);

            return (
              <div className="col-md-4 mb-4" key={doc.id}>
                <article
                  className={`doctor-card doctor-card--${statusInfo.color}`}
                >
                  <header className="doctor-card__header">
                    <div className="doctor-card__avatar-wrap">
                      <img
                        src={photo}
                        alt={name}
                        className="doctor-card__avatar"
                      />
                    </div>

                    <div className="doctor-card__title">
                      <h3 className="doctor-card__name">{name}</h3>
                      <p className="doctor-card__speciality">{speciality}</p>
                      <p className="doctor-card__clinic">{doc.clinic_name}</p>
                    </div>

                    <span
                      className={`doctor-card__status doctor-card__status--${statusInfo.color}`}
                    >
                      {statusInfo.text}
                    </span>
                  </header>

                  <section className="doctor-card__body">
                    {doc.is_free_token === 1 && (
                      <div className="small text-success mb-2">
                        <i className="fas fa-info-circle me-1"></i> No token charges will apply
                      </div>
                    )}
                    <div className="doctor-card__time-row">
                      {slots.map((slot, i) => (
                        <span className="doctor-card__time-chip" key={i}>
                          <span className="doctor-card__time-icon">🕒</span>
                          {slot.start_time} – {slot.end_time}
                        </span>
                      ))}
                    </div>

                    <div className="doctor-card__meta-box">
                      <span className="doctor-card__meta-label">Address</span>
                      <span>{doc.street_address}</span>
                    </div>

                    {/* Fees section commented out - keeping box structure intact */}
                    {/* {doc.doc_fees != null && (
                      <div className="doctor-card__meta-box">
                        <span className="doctor-card__meta-label">Fees</span>
                        <span>₹{doc.doc_fees}</span>
                      </div>
                    )} */}
                  </section>

                  <footer className="doctor-card__footer">
                    {!canBook ? (
                      <div className="text-danger fw-bold">Not Available Today</div>
                    ) : (
                      <button
                        className="btn view-profile-btn w-100 btn-success"
                        onClick={() => handleViewProfile(doc.id)}
                        disabled={loadingDoctorId === doc.id}
                      >
                        {loadingDoctorId === doc.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading...
                          </>
                        ) : (
                          <>View Profile & Book</>
                        )}
                      </button>
                    )}

                  </footer>
                </article>
              </div>
            );
          })}
        </div>
        {/* View all button */}
        <div className="text-center mt-3">
          <button
            className="btn pagerender-btn px-4"
            onClick={() => navigate("/alldoctors")}
          >
            View All Doctors
          </button>
        </div>
      </div>
    </section>
  );
};

export default DoctorsList;
